import React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box, Grid, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, Paper, IconButton, Collapse, Chip, Autocomplete, CircularProgress
} from '@mui/material';
import { getRequest, deleteRequest, putRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, DEL_URL, PUT_URL, POST_URL } from 'Includes/urls';
import { Link } from 'react-router-dom';
import { AddCircleOutlineOutlined, Delete, Edit } from "@mui/icons-material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Swal from 'sweetalert2';
import CreateAnotherExpenseModal from './CreateAnotherExpenseModal';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer_unaccountable', label: 'Bank Transfer (Unaccountable)' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'gateway', label: 'Payment Gateway' },
];

const ADD_NEW_TYPE = '__add_new__';

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, 64);
}

const emptyEditForm = () => ({
  company_id: '',
  expense_type: '',
  reciever: '',
  expense_date: '',
  amount: '',
  quantity: 1,
  mop: 'upi',
  reference: '',
  notes: '',
});

function ExpenseList(props) {

  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  const [expenseTypes, setExpenseTypes] = React.useState([]);
  const [receivers, setReceivers] = React.useState([]);

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  const [filters, setFilters] = React.useState({
    expense_type: '',
    date_from: '',
    date_to: '',
    mop: '',
  });

  const [searchInput, setSearchInput] = React.useState('');
  const [searchKey, setSearchKey] = React.useState('');

  const [summary, setSummary] = React.useState({
    total_count: 0,
    total_amount: 0,
    mop_breakdown: [],
  });

  const [summaryExpanded, setSummaryExpanded] = React.useState(false);
  const [summaryLoading, setSummaryLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);

  const [selectedRows, setSelectedRows] = React.useState([]);
  const [createAnotherDialogOpen, setCreateAnotherDialogOpen] = React.useState(false);

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingExpense, setEditingExpense] = React.useState(null);
  const [editFormData, setEditFormData] = React.useState(emptyEditForm());
  const [editErrors, setEditErrors] = React.useState({});
  const [saveLoading, setSaveLoading] = React.useState(false);

  const [companies, setCompanies] = React.useState([]);
  const [companySearchText, setCompanySearchText] = React.useState('');
  const [companiesLoading, setCompaniesLoading] = React.useState(false);

  const [typeDialogOpen, setTypeDialogOpen] = React.useState(false);
  const [newTypeName, setNewTypeName] = React.useState('');
  const [newTypeDescription, setNewTypeDescription] = React.useState('');
  const [savingType, setSavingType] = React.useState(false);

  const buildFilterParams = React.useCallback(() => ({
    ...(filters.expense_type ? { expense_type: filters.expense_type } : {}),
    ...(filters.date_from ? { date_from: filters.date_from } : {}),
    ...(filters.date_to ? { date_to: filters.date_to } : {}),
    ...(filters.mop ? { mop: filters.mop } : {}),
    ...(searchKey ? { search: searchKey } : {}),
  }), [filters, searchKey]);

  const getExpensesList = React.useCallback(() => {
    setLoading(true);
    const params = {
      limit: pageSize,
      pageno: page + 1,
      ...buildFilterParams(),
    };
    getRequest(GET_URL.expenses.api, params, props)
      .then(res => {
        setLoading(false);
        const data = res?.data?.data || [];
        setRows(data);
        setRowCount(res?.data?.count || data.length);
      })
      .catch(() => setLoading(false));
  }, [page, pageSize, buildFilterParams, props]);

  const getSummary = React.useCallback(() => {
    setSummaryLoading(true);
    getRequest(GET_URL.expenses.api, {
      summary: 1,
      ...buildFilterParams(),
    }, props)
      .then(res => {
        setSummaryLoading(false);
        setSummary(res.data || {});
      })
      .catch(() => setSummaryLoading(false));
  }, [buildFilterParams, props]);

  const fetchReceivers = React.useCallback(() => {
    return getRequest(GET_URL.expense_receivers.api, {}, props).then(response => {
      if (response && response.status === 200) {
        const data = response.data.data || response.data || [];
        setReceivers(Array.isArray(data) ? data : []);
        return data;
      }
      return [];
    }).catch(() => []);
  }, [props]);

  const handleEdit = (row) => {
    setEditingExpense(row);
    setEditFormData({
      company_id: row.company || '',
      expense_type: row.expense_type || '',
      reciever: row.reciever || '',
      expense_date: row.expense_date || '',
      amount: row.amount || '',
      quantity: row.quantity ?? 1,
      mop: row.mop || 'upi',
      reference: row.reference || '',
      notes: row.notes || '',
    });
    setEditErrors({});
    setCompanySearchText(row.company_name || '');
    setEditDialogOpen(true);
  };

  const handleDelete = (row) => {
    deleteRequest(`${DEL_URL.expenses.api}${row.id}/`, {}, props)
      .then((res) => {
        if (res?.status === 200) {
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: 'Deleted',
            text: 'Expense deleted successfully',
            showConfirmButton: false,
            timer: 1500,
          });
          getExpensesList();
          getSummary();
        }
      });
  };

  const handleEditSave = () => {
    const errors = {};
    if (!editFormData.expense_type) errors.expense_type = 'Expense Type is required';
    if (!editFormData.reciever || !String(editFormData.reciever).trim()) errors.reciever = 'Receiver is required';
    if (!editFormData.expense_date) errors.expense_date = 'Expense Date is required';
    if (!editFormData.amount || parseFloat(editFormData.amount) <= 0) errors.amount = 'Amount is required and must be greater than 0';
    if (!editFormData.quantity || parseInt(editFormData.quantity, 10) <= 0) errors.quantity = 'Quantity is required and must be greater than 0';
    if (!editFormData.mop) errors.mop = 'Payment Method is required';

    if (Object.keys(errors).length) {
      setEditErrors(errors);
      return;
    }

    setSaveLoading(true);
    putRequest(
      `${PUT_URL.expenses.api}${editingExpense.id}/`,
      {
        company_id: editFormData.company_id ? Number(editFormData.company_id) : null,
        expense_type: editFormData.expense_type,
        reciever: String(editFormData.reciever).trim(),
        expense_date: editFormData.expense_date,
        amount: parseFloat(editFormData.amount),
        quantity: parseInt(editFormData.quantity, 10),
        mop: editFormData.mop,
        reference: (editFormData.reference || '').trim(),
        notes: (editFormData.notes || '').trim(),
      },
      props
    ).then((res) => {
      setSaveLoading(false);
      if (res?.status === 200) {
        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: 'Updated',
          text: 'Expense updated successfully',
          showConfirmButton: false,
          timer: 1500,
        });
        setEditDialogOpen(false);
        getExpensesList();
        getSummary();
      }
    }).catch((error) => {
      setSaveLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.Reason || 'Failed to update expense',
        timer: 2000,
        showConfirmButton: false,
      });
    });
  };

  const fetchExpenseTypes = React.useCallback(() => {
    return getRequest(GET_URL.expense_types.api, {}, props).then((res) => {
      const data = res?.data?.data || [];
      setExpenseTypes(data);
      return data;
    }).catch(() => []);
  }, [props]);

  const saveExpenseType = () => {
    const name = (newTypeName || '').trim();
    if (!name) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Expense type name is required', timer: 1500, showConfirmButton: false });
      return;
    }
    const code = slugify(name);
    if (!code) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Could not generate a valid code from the name', timer: 1500, showConfirmButton: false });
      return;
    }
    setSavingType(true);
    postRequest(POST_URL.expense_types.api, [{
      code,
      name,
      description: (newTypeDescription || '').trim(),
    }], props)
      .then((response) => {
        if (response && response.status === 200) {
          return fetchExpenseTypes().then((types) => {
            const created = (Array.isArray(types) ? types : []).find((t) => t.code === code || t.name === name);
            if (created) {
              setEditFormData((prev) => ({ ...prev, expense_type: created.id }));
              setEditErrors((prev) => ({ ...prev, expense_type: '' }));
            }
            setTypeDialogOpen(false);
            setNewTypeName('');
            setNewTypeDescription('');
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.Reason || error?.response?.data?.code?.[0] || 'Failed to add expense type',
          timer: 2000,
          showConfirmButton: false,
        });
      })
      .finally(() => setSavingType(false));
  };

  const handleDownloadExcel = () => {
    setExporting(true);
    getRequest(
      GET_URL.expenses.api,
      { export: 1, ...buildFilterParams() },
      { ...props, responseType: 'blob' }
    )
      .then((response) => {
        if (response && response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
          const fileURL = window.URL.createObjectURL(blob);
          const contentDisposition =
            response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
          const match = typeof contentDisposition === 'string'
            ? contentDisposition.match(/filename="?([^"]+)"?/i)
            : null;
          const filename = match?.[1] || 'expenses.csv';
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(fileURL);
          document.body.removeChild(link);
        }
      })
      .finally(() => setExporting(false));
  };

  const columns = [
    {
      field: 'company_name',
      headerName: 'Company',
      minWidth: 180,
      flex: 0.9,
      renderCell: (p) => p.value || '-',
    },
    {
      field: 'expense_type_name',
      headerName: 'Expense Type',
      minWidth: 170,
      flex: 0.8,
      valueGetter: (params) =>
        params?.row?.expense_type_name ||
        params?.row?.expense_type_details?.name ||
        '-',
      renderCell: (p) => p.value || '-',
    },
    {
      field: 'reciever',
      headerName: 'Receiver',
      minWidth: 160,
      flex: 0.9,
      renderCell: (p) => p.value || '-',
    },
    {
      field: 'expense_date',
      headerName: 'Date',
      minWidth: 130,
      flex: 0.7,
      renderCell: (p) => p.value ? new Date(p.value).toLocaleDateString() : '-'
    },
    {
      field: 'amount',
      headerName: 'Amount',
      minWidth: 120,
      flex: 0.6,
      renderCell: (p) => `₹${parseFloat(p.value || 0).toFixed(2)}`
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      minWidth: 110,
      flex: 0.5,
      renderCell: (p) => (p.value === 0 || p.value ? p.value : '-'),
    },
    {
      field: 'mop',
      headerName: 'Mode',
      minWidth: 140,
      flex: 0.7,
      renderCell: (p) => <Chip size="small" variant="outlined" label={(p.value || '-').toString().replaceAll('_', ' ')} />,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem key="edit" icon={<Edit />} label="Edit" onClick={() => handleEdit(params.row)} showInMenu />,
        <GridActionsCellItem key="delete" icon={<Delete />} label="Delete" onClick={() => handleDelete(params.row)} showInMenu />
      ]
    }
  ];

  React.useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchKey((prev) => {
        if (prev !== trimmed) setPage(0);
        return trimmed;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  React.useEffect(() => {
    fetchExpenseTypes();
    fetchReceivers();
  }, []);

  React.useEffect(() => {
    getExpensesList();
    getSummary();
  }, [page, pageSize, filters, searchKey]);

  React.useEffect(() => {
    setPage(0);
  }, [filters]);

  React.useEffect(() => {
    if (!editDialogOpen) return;
    const timer = setTimeout(() => {
      setCompaniesLoading(true);
      getRequest(
        GET_URL.company.api,
        { limit: 10, pageno: 1, search: (companySearchText || '').trim() },
        props
      )
        .then((response) => {
          if (response && response.status === 200) {
            const data = response.data?.data || [];
            if (
              editingExpense?.company &&
              !data.some((c) => Number(c.id) === Number(editingExpense.company))
            ) {
              data.unshift({
                id: editingExpense.company,
                name: editingExpense.company_name || '',
              });
            }
            setCompanies(data);
          } else {
            setCompanies([]);
          }
        })
        .catch(() => setCompanies([]))
        .finally(() => setCompaniesLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [editDialogOpen, companySearchText, editingExpense, props]);

  const selectedCompany =
    (companies || []).find((c) => Number(c.id) === Number(editFormData.company_id)) ||
    (editFormData.company_id
      ? { id: editFormData.company_id, name: editingExpense?.company_name || '' }
      : null);

  return (
    <Box sx={{ p: 3 }}>

      {/* HEADER */}
      <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontWeight={600}>
              Expense List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track your expenses
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} textAlign="right">
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadOutlinedIcon />}
                onClick={handleDownloadExcel}
                disabled={exporting}
              >
                {exporting ? 'Downloading...' : 'Download Excel'}
              </Button>

              <Button
                variant="contained"
                component={Link}
                to="/expenses/expense/add"
                startIcon={<AddCircleOutlineOutlined />}
              >
                Add Expense
              </Button>
            </Box>
          </Grid>
        </Grid>

      {/* SUMMARY */}
      <Box mb={2} p={2} sx={{ background: '#f8fafc', borderRadius: 2, border: '1px solid #e5e7eb' }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setSummaryExpanded(!summaryExpanded)}
          sx={{ cursor: 'pointer' }}
        >
          <Typography fontWeight={600}>Summary</Typography>

          <Box display="flex" alignItems="center" gap={2}>
            {!summaryExpanded && (
              <Typography fontWeight={700}>
                ₹{summary.total_amount || 0}
              </Typography>
            )}
            <IconButton size="small">
              {summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={summaryExpanded}>
          <Box mt={2}>
            <Typography>Total Count: {summaryLoading ? '...' : summary.total_count}</Typography>
            <Typography>Total Amount: ₹{summary.total_amount}</Typography>
          </Box>
        </Collapse>
      </Box>

      {/* FILTERS */}
      <Paper elevation={0} sx={{ p: 2, mt: 3, mb: 2, borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={2}>

          <Grid item xs={12} md={3}>
            <TextField
              label="Search"
              fullWidth
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Expense Type</InputLabel>
              <Select
                value={filters.expense_type}
                onChange={(e) => setFilters({ ...filters, expense_type: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {expenseTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              type="date"
              label="From"
              size="small"
              fullWidth
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              type="date"
              label="To"
              size="small"
              fullWidth
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              sx={{ height: '40px', textTransform: 'none' }}
              onClick={() => {
                setFilters({ expense_type: '', date_from: '', date_to: '', mop: '' });
                setSearchInput('');
                setSearchKey('');
              }}
            >
              Clear
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* TABLE */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", boxShadow: "0 6px 20px rgba(0,0,0,0.05)" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          pagination
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: 600,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
          }}
        />
      </Paper>

      {/* CREATE ANOTHER */}
      <CreateAnotherExpenseModal
        open={createAnotherDialogOpen}
        onClose={() => setCreateAnotherDialogOpen(false)}
        selectedExpenses={rows.filter(r => selectedRows.includes(r.id))}
        expenseTypes={expenseTypes}
        props={props}
      />

      {/* EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Expense</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={companies || []}
                  getOptionLabel={(option) => option?.name || ''}
                  value={selectedCompany}
                  onChange={(_, selected) => setEditFormData((prev) => ({ ...prev, company_id: selected?.id || '' }))}
                  isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                  onInputChange={(_, value) => setCompanySearchText(value || '')}
                  loading={companiesLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Company (Optional)"
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="Search company"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!editErrors.expense_type}>
                  <InputLabel>Expense Type</InputLabel>
                  <Select
                    value={editFormData.expense_type || ''}
                    label="Expense Type"
                    onChange={(e) => {
                      if (e.target.value === ADD_NEW_TYPE) {
                        setNewTypeName('');
                        setNewTypeDescription('');
                        setTypeDialogOpen(true);
                        return;
                      }
                      setEditFormData((prev) => ({ ...prev, expense_type: e.target.value }));
                      setEditErrors((prev) => ({ ...prev, expense_type: '' }));
                    }}
                  >
                    {expenseTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                    ))}
                    <MenuItem value={ADD_NEW_TYPE}>Add New</MenuItem>
                  </Select>
                  {editErrors.expense_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {editErrors.expense_type}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!editErrors.reciever}>
                  <InputLabel>Receiver</InputLabel>
                  <Select
                    value={editFormData.reciever || ''}
                    label="Receiver"
                    onChange={(e) => {
                      setEditFormData((prev) => ({ ...prev, reciever: e.target.value }));
                      setEditErrors((prev) => ({ ...prev, reciever: '' }));
                    }}
                  >
                    {receivers.map((receiver) => (
                      <MenuItem key={receiver.id} value={receiver.name}>{receiver.name}</MenuItem>
                    ))}
                    {editFormData.reciever && !receivers.some((r) => r.name === editFormData.reciever) && (
                      <MenuItem value={editFormData.reciever}>{editFormData.reciever}</MenuItem>
                    )}
                  </Select>
                  {editErrors.reciever && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {editErrors.reciever}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Expense Date"
                  type="date"
                  fullWidth
                  size="small"
                  required
                  value={editFormData.expense_date || ''}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, expense_date: e.target.value }))}
                  error={!!editErrors.expense_date}
                  helperText={editErrors.expense_date || ''}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Amount"
                  type="number"
                  fullWidth
                  size="small"
                  required
                  value={editFormData.amount || ''}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, amount: e.target.value }))}
                  error={!!editErrors.amount}
                  helperText={editErrors.amount || ''}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Quantity"
                  type="number"
                  fullWidth
                  size="small"
                  required
                  value={editFormData.quantity ?? ''}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, quantity: e.target.value }))}
                  error={!!editErrors.quantity}
                  helperText={editErrors.quantity || ''}
                  inputProps={{ min: 1, step: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!editErrors.mop}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={editFormData.mop || 'upi'}
                    label="Payment Method"
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, mop: e.target.value }))}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reference"
                  fullWidth
                  size="small"
                  value={editFormData.reference || ''}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, reference: e.target.value }))}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  value={editFormData.notes || ''}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={saveLoading} sx={{ textTransform: 'none' }}>
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={typeDialogOpen} onClose={() => setTypeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Expense Type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            required
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={newTypeDescription}
            onChange={(e) => setNewTypeDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTypeDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveExpenseType} disabled={savingType} sx={{ textTransform: 'none' }}>
            {savingType ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default ExpenseList;
